import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@hero/context';
import { useTranslation } from 'react-i18next';
import { getStaticRegion, type StaticRegionLang } from '@hero/lang';
import type { Language } from '@hero/schema';
import type { RegionTranslateEntry } from '@hero/api';

/**
 * Layered render-time translation for a batch of region customCodes.
 *
 * Resolution order — see `docs/regions/01-translation-and-cache.md`.
 *
 * 1. **L1 — static bundle (`@hero/lang`).** Zero-network. Every country / state /
 *    governorate / hub + major city ships in `saudi.json`. Hits cover ~95% of
 *    real-world traffic.
 * 2. **L2 — TanStack Query.** Anything missing from L1 is batched into a single
 *    `api.common.region.translate(...)` call. The query is keyed by
 *    `(missingCodes, lang)` and persists across sessions (24h staleTime +
 *    Infinity gcTime per the `region` entity config).
 * 3. **L3 — Backend.** `region.translate` consults `RegionCacheService` (L4
 *    in-memory) then Mongo (L5). Edge-cached via `Cache-Control: max-age=86400 +
 *    ETag`, so the CDN absorbs the shared traffic.
 *
 * The hook returns a `Map<customCode, RegionTranslateEntry>` so consumers can
 * synchronously read each entry while it's loading (missing codes hit L1 only,
 * fully-resolved codes always render in the requested language). `isLoading`
 * is only `true` while L2 is fetching new misses.
 *
 * @example
 * const customCodes = ['riyadh', 'jeddah'];
 * const { entries, isLoading } = useTranslateRegions(customCodes);
 * return <span>{entries.get('riyadh')?.displayName ?? 'riyadh'}</span>;
 */
export interface UseTranslateRegionsResult {
  /** Resolved translations keyed by lowercased `customCode`. */
  entries: Map<string, RegionTranslateEntry>;
  /** True while the L2 query is in flight (network misses only). */
  isLoading: boolean;
  /** Set if the L2 batch errored. L1 hits are still served. */
  error: Error | null;
  /** Convenience helper — `display('riyadh')` returns the localized name or the raw code. */
  display: (customCode: string) => string;
}

/** Map the app's `Language` (which can be wider than `StaticRegionLang`) to the static bundle's lang. */
function toStaticLang(lang: Language | undefined, fallback: StaticRegionLang = 'en'): StaticRegionLang {
  if (lang === 'ar' || lang === 'en' || lang === 'ur') return lang;
  return fallback;
}

/**
 * Max codes per `GET /region/translate` call — mirrors the backend's
 * `regionTranslateAPISchema` `.max(100)`. Bigger miss sets are chunked.
 */
const TRANSLATE_BATCH_LIMIT = 100;

/** Lowercase + dedupe a list of customCodes for stable cache keys. */
function normalizeCodes(codes: readonly string[]): string[] {
  const seen = new Set<string>();
  for (const code of codes) {
    if (!code) continue;
    const lower = code.toLowerCase();
    seen.add(lower);
  }
  return Array.from(seen).sort();
}

export function useTranslateRegions(
  customCodes: readonly string[] | undefined,
  langOverride?: Language,
): UseTranslateRegionsResult {
  const api = useApi();
  const { i18n } = useTranslation();
  const lang = toStaticLang((langOverride ?? (i18n.language as Language)) ?? 'en');

  const normalized = useMemo(() => normalizeCodes(customCodes ?? []), [customCodes]);

  // L1 — static bundle. Build the entries we already know synchronously so the
  // first render doesn't wait on the network round-trip.
  const { l1Entries, l1Misses } = useMemo(() => {
    const hits = new Map<string, RegionTranslateEntry>();
    const misses: string[] = [];
    for (const code of normalized) {
      const staticHit = getStaticRegion(code, lang);
      if (staticHit) {
        hits.set(code, {
          customCode: code,
          displayName: staticHit.displayName,
          type: staticHit.type as RegionTranslateEntry['type'],
        });
      } else {
        misses.push(code);
      }
    }
    return { l1Entries: hits, l1Misses: misses };
  }, [normalized, lang]);

  // L2 — TanStack Query for the L1 misses. Disabled when there's nothing to fetch.
  const query = useQuery({
    queryKey: ['region-translate', lang, l1Misses],
    queryFn: async () => {
      if (l1Misses.length === 0) return {} as Record<string, RegionTranslateEntry>;
      // The endpoint caps a batch at 100 codes (`regionTranslateAPISchema`
      // `.max(100)`) — chunk the misses and merge. Parallel is fine: the
      // responses are edge-cached and independent.
      const chunks: string[][] = [];
      for (let i = 0; i < l1Misses.length; i += TRANSLATE_BATCH_LIMIT) {
        chunks.push(l1Misses.slice(i, i + TRANSLATE_BATCH_LIMIT));
      }
      const results = await Promise.all(
        chunks.map(customCodes => api.common.region.translate({ customCodes, lang })),
      );
      return Object.assign(
        {} as Record<string, RegionTranslateEntry>,
        ...results,
      ) as Record<string, RegionTranslateEntry>;
    },
    enabled: l1Misses.length > 0,
    // Region docs change ~once per quarter (importer run) → 24h staleTime is generous.
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: Number.POSITIVE_INFINITY,
  });

  // Merge L1 + L2.
  const entries = useMemo(() => {
    const out = new Map<string, RegionTranslateEntry>(l1Entries);
    if (query.data) {
      for (const [code, entry] of Object.entries(query.data)) {
        out.set(code.toLowerCase(), entry);
      }
    }
    return out;
  }, [l1Entries, query.data]);

  const display = (customCode: string): string => {
    if (!customCode) return '';
    const key = customCode.toLowerCase();
    return entries.get(key)?.displayName ?? customCode;
  };

  return {
    entries,
    isLoading: query.isLoading && l1Misses.length > 0,
    error: query.error ?? null,
    display,
  };
}
